import { type KeyboardEvent, type MouseEvent } from 'react'
import {
  type CrossClientQueryClientQueryMode,
  CrossClientQueryClientQueryModes,
  type CrossClientQueryDataSource,
  type CrossClientQueryExecution,
} from '../../models/CrossClient'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { type User } from '../../models/User'
import { type IntegrationServiceId } from '../../common/integrations'
import { getCrossClientIntegrationDisplayName } from './integrationDisplay'

// What the landing's init screen showed for a query it just started, so the results page carries that screen on.
export interface CrossClientStartedQuery {
  question: string
  criteria: string[]
  // Epoch ms.
  shownSince: number
}

// Location state the landing sets when it opens the results of a query it just started; a reload after the results
// page drops it, and a list click never carries it.
export const justStartedState = (startedQuery: CrossClientStartedQuery) => ({ startedQuery })

export const getStartedQuery = (state: unknown): CrossClientStartedQuery | undefined =>
  (state as Partial<ReturnType<typeof justStartedState>> | null)?.startedQuery

// Make a `div role="button"` keyboard-activatable: runs the handler on Enter or Space keydown. (Native
// buttons fire Space on keyup; this fires both on keydown, which is fine for these click-only rows.)
export const onActivateKeyDown =
  (handler: () => void) =>
  (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handler()
    }
  }

// Row click that skips clicks a nested control already handled (it calls preventDefault instead of stopPropagation,
// so document-level listeners such as open dropdowns' outside-click close still fire).
export const onUnhandledClick =
  (handler: () => void) =>
  (e: MouseEvent): void => {
    if (!e.defaultPrevented) {
      handler()
    }
  }

export const CLIENT_MODE_LABEL: Record<CrossClientQueryClientQueryMode, string> = {
  [CrossClientQueryClientQueryModes.ONLY_OWNED]: 'Yours only',
  [CrossClientQueryClientQueryModes.WITH_SHARED]: 'Yours + Shared',
  [CrossClientQueryClientQueryModes.ALL_AVAILABLE]: 'All available',
}

export const executionClientQueryMode = (execution: Pick<CrossClientQueryExecution, 'clientQueryMode'>): CrossClientQueryClientQueryMode =>
  execution.clientQueryMode ?? CrossClientQueryClientQueryModes.WITH_SHARED

export const isCrossClientQueryClientQueryMode = (value: unknown): value is CrossClientQueryClientQueryMode =>
  value === CrossClientQueryClientQueryModes.ONLY_OWNED ||
  value === CrossClientQueryClientQueryModes.WITH_SHARED ||
  value === CrossClientQueryClientQueryModes.ALL_AVAILABLE

const ZOCKS_SOURCE_TYPES: Record<CrossClientQueryDataSource['type'], boolean> = {
  clientProfile: true,
  meeting: true,
  meetingMetadata: true,
  aiResult: true,
  task: true,
  clientMetadata: true,
  clientSignal: true,
  integration: false,
}

// Unknown types default to Zocks: only `integration` carries the `integrationType` a third-party icon needs.
export const isZocksSource = (source: CrossClientQueryDataSource): boolean => ZOCKS_SOURCE_TYPES[source.type] ?? true

export const getIntegrationSourceDisplayNames = (sources: CrossClientQueryDataSource[]): string[] =>
  sources
    .filter((source) => source.type === 'integration')
    .map((source) => getCrossClientIntegrationDisplayName(source.integrationType as IntegrationServiceId))

export const pluralize = (count: number, singular: string, plural: string = `${singular}s`): string => (count === 1 ? singular : plural)

export const joinNames = (names: string[], conjunction: 'and' | 'or' = 'and'): string => {
  if (names.length === 0) {
    return ''
  }
  if (names.length === 1) {
    return names[0]
  }
  if (names.length === 2) {
    return `${names[0]} ${conjunction} ${names[1]}`
  }
  return `${names.slice(0, -1).join(', ')}, ${conjunction} ${names[names.length - 1]}`
}

export const getOwnerLabel = (currentUser: User, teammates: Record<string, User>, ownerId: string | null) => {
  if (!ownerId) {
    return '-'
  }
  if (ownerId === currentUser.id) {
    return `${getUserDisplayName(currentUser)} (You)`
  }
  const owner = teammates[ownerId]
  return owner ? getUserDisplayName(owner) : '-'
}
